import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CalendarBlank, Plus, PencilSimple, Trash, Check, X, CircleDashed, Square, Rectangle } from "@phosphor-icons/react";
import { format } from "date-fns";
import { useRestaurant } from "../contexts/RestaurantContext";

const STATUS_COLORS = {
  confirmed: "#2F4F4F",
  seated: "#81B29A",
  completed: "#57534E",
  cancelled: "#9CA3AF",
};

const TIER_CONFIG = {
  gold: { label: "Gold", bg: "#D4AF3720", color: "#B8860B", border: "#D4AF37" },
  silver: { label: "Silver", bg: "#C0C0C020", color: "#6B7280", border: "#A8A8A8" },
  bronze: { label: "Bronze", bg: "#CD7F3220", color: "#A0522D", border: "#CD7F32" },
};
const TierBadge = ({ tier }) => {
  if (!tier || !TIER_CONFIG[tier]) return null;
  const c = TIER_CONFIG[tier];
  return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border" style={{ backgroundColor: c.bg, color: c.color, borderColor: c.border }} data-testid={`tier-badge-${tier}`}>{c.label}</span>;
};

const STATUS_TABLE_COLORS = { available: "#81B29A", occupied: "#E07A5F", reserved: "#F4A261", blocked: "#9CA3AF" };
const ShapeIcon = ({ shape }) => shape === "round" ? <CircleDashed size={14} /> : shape === "rectangle" ? <Rectangle size={14} /> : <Square size={14} />;

const timeSlots = (() => {
  const slots = [];
  for (let h = 11; h <= 22; h++) {
    const suffix = h >= 12 ? "PM" : "AM";
    const display = h > 12 ? h - 12 : h;
    slots.push({ value: `${display}:00 ${suffix}`, label: `${display}:00 ${suffix}` });
    slots.push({ value: `${display}:30 ${suffix}`, label: `${display}:30 ${suffix}` });
  }
  return slots;
})();

function ResForm({ onSubmit, buttonLabel, form, setForm, allTables, floors, pickerFloor, setPickerFloor }) {
  const floorTablesMap = {};
  allTables.forEach((t) => {
    if (!floorTablesMap[t.floor_id]) floorTablesMap[t.floor_id] = [];
    floorTablesMap[t.floor_id].push(t);
  });
  const activePickerFloor = pickerFloor || (floors.length > 0 ? floors[0].id : "");
  const visibleTables = floorTablesMap[activePickerFloor] || [];
  const selectedIds = form.table_ids || [];
  const totalSeats = visibleTables.filter((t) => selectedIds.includes(t.id)).reduce((a, t) => a + t.seats, 0);

  const toggleTable = (t) => {
    if (t.status !== "available" && !selectedIds.includes(t.id)) return;
    const newIds = selectedIds.includes(t.id)
      ? selectedIds.filter((id) => id !== t.id)
      : [...selectedIds, t.id];
    setForm({ ...form, table_ids: newIds, table_id: newIds[0] || "", floor_id: activePickerFloor });
  };

  return (
    <div className="space-y-4 mt-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Member(s) Name</Label>
          <Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="mt-1.5" data-testid="res-guest-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Member Number</Label>
          <Input value={form.member_number} onChange={(e) => setForm({ ...form, member_number: e.target.value })} placeholder="e.g. M001" className="mt-1.5" data-testid="res-member-number" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Phone</Label>
          <Input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className="mt-1.5" data-testid="res-guest-phone" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Party Size</Label>
          <Input type="number" min={1} max={20} value={form.party_size} onChange={(e) => setForm({ ...form, party_size: parseInt(e.target.value) || "" })} onFocus={(e) => e.target.select()} onBlur={(e) => { if (!e.target.value) setForm({ ...form, party_size: 1 }); }} className="mt-1.5" data-testid="res-party-size" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Time</Label>
          <Select value={form.time} onValueChange={(v) => setForm({ ...form, time: v })}>
            <SelectTrigger className="mt-1.5" data-testid="res-time-select"><SelectValue>{form.time}</SelectValue></SelectTrigger>
            <SelectContent>
              {timeSlots.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Notes</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Special requests" className="mt-1.5" data-testid="res-notes" />
        </div>
      </div>

      {/* Multi-Select Table Picker */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Assign Tables</Label>
          {selectedIds.length > 0 && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#2F4F4F]/10 text-[#2F4F4F]">
              {selectedIds.length} table{selectedIds.length > 1 ? "s" : ""} · {totalSeats} seats
              {selectedIds.length > 1 && " (combined)"}
            </span>
          )}
        </div>
        {floors.length > 0 && (
          <div className="flex gap-1 mb-2">
            {floors.map((f) => (
              <button
                key={f.id}
                className={`text-xs px-2.5 py-1 rounded-md transition-all font-medium ${activePickerFloor === f.id ? "bg-[#2F4F4F] text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}
                onClick={() => setPickerFloor(f.id)}
                data-testid={`picker-floor-${f.id}`}
              >
                {f.name}
              </button>
            ))}
          </div>
        )}
        <div className="grid grid-cols-4 gap-1.5 max-h-[180px] overflow-y-auto p-1" data-testid="table-picker-grid">
          <button
            className={`flex flex-col items-center p-2 rounded-lg border transition-all text-xs ${selectedIds.length === 0 ? "border-[#2F4F4F] bg-[#2F4F4F]/5 ring-1 ring-[#2F4F4F]" : "border-stone-200 hover:border-stone-300"}`}
            onClick={() => setForm({ ...form, table_ids: [], table_id: "", floor_id: "" })}
            data-testid="table-pick-auto"
          >
            <span className="text-[10px] text-stone-500">Auto</span>
            <span className="font-medium">Later</span>
          </button>
          {visibleTables.map((t) => {
            const isSelected = selectedIds.includes(t.id);
            const fits = t.seats >= (form.party_size || 1);
            const isAvail = t.status === "available" || isSelected;
            return (
              <button
                key={t.id}
                className={`flex flex-col items-center p-2 rounded-lg border transition-all text-xs relative ${isSelected ? "border-[#2F4F4F] bg-[#2F4F4F]/5 ring-1 ring-[#2F4F4F]" : isAvail ? "border-stone-200 hover:border-stone-300" : "border-stone-100 opacity-40 cursor-not-allowed"}`}
                onClick={() => toggleTable(t)}
                disabled={!isAvail}
                data-testid={`table-pick-${t.name}`}
              >
                {/* Checkbox indicator */}
                <div className={`absolute top-1 right-1 w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${isSelected ? "bg-[#2F4F4F] border-[#2F4F4F]" : "border-stone-300 bg-white"}`}>
                  {isSelected && <Check size={10} className="text-white" weight="bold" />}
                </div>
                <div className="w-5 h-5 rounded flex items-center justify-center mb-0.5" style={{ backgroundColor: STATUS_TABLE_COLORS[t.status] + "30" }}>
                  <ShapeIcon shape={t.shape} />
                </div>
                <span className="font-semibold truncate w-full text-center">{t.name}</span>
                <span className="text-[10px] text-stone-500">{t.seats} seats</span>
                {isAvail && !isSelected && !fits && <span className="text-[9px] text-red-400 font-medium">small</span>}
                {isAvail && !isSelected && fits && <span className="text-[9px] text-green-500 font-medium">fits</span>}
              </button>
            );
          })}
        </div>
      </div>

      <Button onClick={onSubmit} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="res-submit-btn">{buttonLabel}</Button>
    </div>
  );
}

export default function ReservationsPage() {
  const { restId } = useRestaurant();
  const [reservations, setReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [floors, setFloors] = useState([]);
  const [tables, setTables] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingRes, setEditingRes] = useState(null);
  const [form, setForm] = useState({
    guest_name: "", guest_phone: "", member_number: "", party_size: 2,
    date: format(new Date(), "yyyy-MM-dd"), time: "7:00 PM",
    table_id: "", table_ids: [], floor_id: "", notes: "",
  });
  const [allTables, setAllTables] = useState([]);
  const [pickerFloor, setPickerFloor] = useState("");
  const [tiers, setTiers] = useState({});

  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const fetchReservations = useCallback(async () => {
    try {
      const { data } = await api.get(`/reservations?date=${dateStr}&type=reservation`);
      setReservations(data);
    } catch { toast.error("Failed to load reservations"); }
  }, [dateStr]);

  const fetchTiers = useCallback(async () => {
    try {
      const { data } = await api.get(`/reservations/tiers?date=${dateStr}`);
      setTiers(data);
    } catch { /* silent */ }
  }, [dateStr]);

  const fetchFloors = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/floors${params}`);
      setFloors(data);
    } catch { /* silent */ }
  }, [restId]);

  useEffect(() => { fetchReservations(); }, [fetchReservations]);
  useEffect(() => { fetchFloors(); }, [fetchFloors]);
  useEffect(() => { fetchTiers(); }, [fetchTiers]);

  const fetchAllTables = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/tables/all${params}`);
      setAllTables(data);
    } catch { /* silent */ }
  }, [restId]);

  useEffect(() => { fetchAllTables(); }, [fetchAllTables]);

  const fetchTables = async (floorId) => {
    if (!floorId) return;
    try {
      const { data } = await api.get(`/floors/${floorId}/tables`);
      setTables(data.filter((t) => t.status === "available"));
    } catch { /* silent */ }
  };

  const createReservation = async () => {
    if (!form.guest_name.trim()) return;
    try {
      const payload = {
        ...form,
        table_id: form.table_ids.length > 0 ? form.table_ids[0] : null,
        table_ids: form.table_ids,
        floor_id: form.floor_id || null,
      };
      await api.post("/reservations", payload);
      setShowAdd(false);
      setForm({ guest_name: "", guest_phone: "", member_number: "", party_size: 2, date: dateStr, time: "7:00 PM", table_id: "", table_ids: [], floor_id: "", notes: "" });
      fetchReservations();
      fetchAllTables();
      toast.success("Reservation created");
    } catch { toast.error("Failed to create reservation"); }
  };

  const updateReservation = async () => {
    if (!editingRes) return;
    try {
      const payload = {
        ...form,
        table_id: form.table_ids.length > 0 ? form.table_ids[0] : null,
        table_ids: form.table_ids,
      };
      await api.put(`/reservations/${editingRes.id}`, payload);
      setShowEdit(false);
      setEditingRes(null);
      fetchReservations();
      fetchAllTables();
      toast.success("Reservation updated");
    } catch { toast.error("Failed to update"); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/reservations/${id}`, { status });
      fetchReservations();
      toast.success(`Status: ${status}`);
    } catch { toast.error("Failed"); }
  };

  const deleteReservation = async (id) => {
    try {
      await api.delete(`/reservations/${id}`);
      fetchReservations();
      toast.success("Reservation deleted");
    } catch { toast.error("Failed"); }
  };

  const openEdit = (res) => {
    setEditingRes(res);
    const ids = res.table_ids || [];
    if (res.table_id && !ids.includes(res.table_id)) ids.push(res.table_id);
    setForm({
      guest_name: res.guest_name, guest_phone: res.guest_phone || "",
      member_number: res.member_number || "",
      party_size: res.party_size, date: res.date, time: res.time,
      table_id: res.table_id || "", table_ids: ids, floor_id: res.floor_id || "", notes: res.notes || "",
      status: res.status,
    });
    if (res.floor_id) fetchTables(res.floor_id);
    setShowEdit(true);
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="reservations-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Reservations</h2>
          <p className="text-xs sm:text-sm text-stone-500 mt-1">{format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="text-sm" data-testid="date-picker-btn">
                <CalendarBlank size={16} className="mr-1.5" /> {format(selectedDate, "MMM d")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} data-testid="reservation-calendar" />
            </PopoverContent>
          </Popover>
          <Button onClick={() => { setForm({ guest_name: "", guest_phone: "", member_number: "", party_size: 2, date: dateStr, time: "7:00 PM", table_id: "", table_ids: [], floor_id: "", notes: "" }); setPickerFloor(""); setShowAdd(true); }} style={{ backgroundColor: "#2F4F4F" }} data-testid="add-reservation-btn">
            <Plus size={18} className="mr-1.5" /> New Reservation
          </Button>
        </div>
      </div>

      {/* Timeline View */}
      {reservations.length > 0 && (
        <div className="bg-white rounded-lg border border-stone-200 p-4 mb-4 overflow-x-auto" data-testid="reservation-timeline">
          <p className="text-[9px] uppercase tracking-widest text-stone-400 font-bold mb-3">Timeline</p>
          <div className="relative" style={{ minWidth: 600 }}>
            {/* Hour markers */}
            <div className="flex items-center border-b border-stone-100 pb-1 mb-2">
              {Array.from({ length: 14 }, (_, i) => i + 10).map((h) => (
                <div key={h} className="flex-1 text-center text-[9px] text-stone-400 font-medium">
                  {h > 12 ? h - 12 : h}{h >= 12 ? "p" : "a"}
                </div>
              ))}
            </div>
            {/* Reservation bars */}
            <div className="space-y-1.5">
              {reservations.map((res) => {
                // Parse time to hour position
                let hour = 12;
                const t = res.time || "";
                if (t.includes("AM") || t.includes("PM")) {
                  const parts = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
                  if (parts) {
                    hour = parseInt(parts[1]);
                    if (parts[3].toUpperCase() === "PM" && hour !== 12) hour += 12;
                    if (parts[3].toUpperCase() === "AM" && hour === 12) hour = 0;
                    hour += parseInt(parts[2]) / 60;
                  }
                } else if (t.includes(":")) {
                  const [hh, mm] = t.split(":");
                  hour = parseInt(hh) + parseInt(mm || 0) / 60;
                }
                const startOffset = Math.max(0, ((hour - 10) / 14) * 100);
                const barWidth = Math.max(4, (1.25 / 14) * 100); // ~1.25 hour duration
                const statusColor = STATUS_COLORS[res.status] || "#2F4F4F";
                return (
                  <div key={res.id} className="relative h-7 flex items-center" data-testid={`timeline-bar-${res.id}`}>
                    <div
                      className="absolute h-6 rounded-md flex items-center px-2 gap-1 overflow-hidden cursor-default transition-all hover:brightness-110"
                      style={{
                        left: `${startOffset}%`, width: `${barWidth}%`, minWidth: 60,
                        backgroundColor: statusColor + "25", borderLeft: `3px solid ${statusColor}`,
                      }}
                      title={`${res.guest_name} · Party of ${res.party_size} · ${res.time} · ${res.status}`}
                    >
                      <span className="text-[10px] font-semibold truncate" style={{ color: statusColor }}>{res.guest_name}</span>
                      <span className="text-[9px] text-stone-400 flex-shrink-0">{res.party_size}p</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {reservations.length === 0 ? (
        <div className="text-center py-16 animate-fade-in">
          <CalendarBlank size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
          <p className="text-stone-500 text-sm">No reservations for this date</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reservations.map((res, idx) => (
            <div key={res.id} className="bg-white rounded-lg border border-stone-200 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 animate-fade-in" style={{ animationDelay: `${idx * 40}ms` }} data-testid={`reservation-item-${res.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-12 sm:w-14 text-center flex-shrink-0">
                  <span className="text-base sm:text-lg font-bold" style={{ color: "#2F4F4F", fontFamily: "Work Sans" }}>{res.time}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-stone-800 text-sm sm:text-base">{res.guest_name}</span>
                    {res.member_number && <Badge variant="outline" className="text-[9px]">#{res.member_number}</Badge>}
                    <TierBadge tier={tiers[res.guest_name]} />
                    <Badge style={{ backgroundColor: STATUS_COLORS[res.status] + "20", color: STATUS_COLORS[res.status], border: "none" }}>
                      {res.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 mt-1 text-xs text-stone-500 flex-wrap">
                    <span>Party of {res.party_size}</span>
                    {(() => {
                      const ids = res.table_ids || [];
                      if (res.table_id && !ids.includes(res.table_id)) ids.push(res.table_id);
                      if (ids.length === 0) return null;
                      const names = ids.map((id) => allTables.find((tb) => tb.id === id)?.name).filter(Boolean);
                      return names.length > 0 ? <span className="font-medium text-stone-600 bg-stone-100 px-1.5 py-0.5 rounded">@ {names.join(" + ")}</span> : null;
                    })()}
                    {res.guest_phone && <span className="hidden sm:inline">{res.guest_phone}</span>}
                    {res.notes && <span className="hidden sm:inline">{res.notes}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 ml-12 sm:ml-0">
                {res.status === "confirmed" && (
                  <Button variant="outline" size="sm" onClick={() => changeStatus(res.id, "seated")} className="text-xs text-green-700 hover:bg-green-50" data-testid={`seat-res-${res.id}`}>
                    <Check size={14} className="mr-1" /> Seat
                  </Button>
                )}
                {res.status === "seated" && (
                  <Button variant="outline" size="sm" onClick={() => changeStatus(res.id, "completed")} className="text-xs" data-testid={`complete-res-${res.id}`}>
                    <Check size={14} className="mr-1" /> Complete
                  </Button>
                )}
                {res.status === "completed" && (
                  <Button variant="outline" size="sm" onClick={() => changeStatus(res.id, "seated")} className="text-xs text-amber-600 hover:bg-amber-50 border-amber-300" data-testid={`reopen-res-${res.id}`}>
                    Reopen
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => openEdit(res)} className="text-xs" data-testid={`edit-res-${res.id}`}>
                  <PencilSimple size={14} />
                </Button>
                {res.status !== "cancelled" && (
                  <Button variant="ghost" size="sm" onClick={() => changeStatus(res.id, "cancelled")} className="text-xs text-red-500" data-testid={`cancel-res-${res.id}`}>
                    <X size={14} />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => deleteReservation(res.id)} className="text-xs text-red-400" data-testid={`delete-res-${res.id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-reservation-dialog">
          <DialogHeader><DialogTitle>New Reservation</DialogTitle></DialogHeader>
          <ResForm onSubmit={createReservation} buttonLabel="Create Reservation" form={form} setForm={setForm} allTables={allTables} floors={floors} pickerFloor={pickerFloor} setPickerFloor={setPickerFloor} />
        </DialogContent>
      </Dialog>

      <Dialog open={showEdit} onOpenChange={(open) => { setShowEdit(open); if (!open) { setEditingRes(null); setForm({ guest_name: "", guest_phone: "", member_number: "", party_size: 2, date: dateStr, time: "7:00 PM", table_id: "", table_ids: [], floor_id: "", notes: "" }); setPickerFloor(""); } }}>
        <DialogContent data-testid="edit-reservation-dialog">
          <DialogHeader><DialogTitle>Edit Reservation</DialogTitle></DialogHeader>
          <ResForm onSubmit={updateReservation} buttonLabel="Save Changes" form={form} setForm={setForm} allTables={allTables} floors={floors} pickerFloor={pickerFloor} setPickerFloor={setPickerFloor} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
